/**
 * Publish / embed dialog (legacy EACReportViewType = 8).
 *
 * Lets owners mint tokenised public URLs for a dashboard:
 *   - Create, label, set expiry, revoke
 *   - Copy URL to clipboard
 *   - Read access counter + last access timestamp
 *
 * Public consumers hit /api/v1/reporting/publish/<token>/ with no auth.
 */
import { useCallback, useEffect, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../../components/ui/dialog';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Switch } from '../../../components/ui/switch';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../../components/ui/table';
import { Copy, Link as LinkIcon, Loader2, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { useFormatters } from '../../../contexts/PreferencesContext';
import {
  reportingAPI,
  type AnalyseDashboard,
  type DashboardPublishToken,
} from '../../../lib/api/reporting';

interface PublishReportDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  report: AnalyseDashboard | null;
}

/**
 * Absolute, shareable URL for a publish token. Uses window.location.origin
 * so copying in dev gives localhost and in prod gives the real host.
 */
function absolutePublishUrl(token: string): string {
  const path = reportingAPI.publishedDashboardUrl(token);
  if (typeof window !== 'undefined' && window.location) {
    return `${window.location.origin}${path}`;
  }
  return path;
}

export function PublishReportDialog({
  open,
  onOpenChange,
  report,
}: PublishReportDialogProps) {
  const [tokens, setTokens] = useState<DashboardPublishToken[]>([]);
  const [loading, setLoading] = useState(false);
  const { formatDate, formatDateTime } = useFormatters();
  const [creating, setCreating] = useState(false);
  const [label, setLabel] = useState('');
  const [expiresAt, setExpiresAt] = useState('');

  const loadTokens = useCallback(async (dashboardId: number) => {
    setLoading(true);
    try {
      const response = await reportingAPI.listPublishTokens(dashboardId);
      setTokens(response.data.results || []);
    } catch (err) {
      console.error('Failed to load publish tokens:', err);
      toast.error('Failed to load publish links');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!report || !open) return;
    void loadTokens(report.id);
    setLabel('');
    setExpiresAt('');
  }, [report, open, loadTokens]);

  const handleCreate = async () => {
    if (!report) return;
    setCreating(true);
    try {
      const response = await reportingAPI.createPublishToken({
        dashboard: report.id,
        label: label.trim() || undefined,
        expires_at: expiresAt ? new Date(expiresAt).toISOString() : null,
      });
      setTokens((prev) => [response.data, ...prev]);
      setLabel('');
      setExpiresAt('');
      toast.success('Publish link created');
    } catch (err) {
      console.error('Failed to create publish token:', err);
      toast.error('Failed to create publish link');
    } finally {
      setCreating(false);
    }
  };

  const handleToggleActive = async (t: DashboardPublishToken, active: boolean) => {
    try {
      const response = await reportingAPI.updatePublishToken(t.id, { is_active: active });
      setTokens((prev) => prev.map((x) => (x.id === t.id ? response.data : x)));
    } catch (err) {
      console.error('Failed to update token:', err);
      toast.error('Failed to update publish link');
    }
  };

  const handleDelete = async (t: DashboardPublishToken) => {
    if (!window.confirm(`Revoke "${t.label || 'this link'}" permanently?`)) return;
    try {
      await reportingAPI.deletePublishToken(t.id);
      setTokens((prev) => prev.filter((x) => x.id !== t.id));
      toast.success('Publish link revoked');
    } catch (err) {
      console.error('Failed to delete token:', err);
      toast.error('Failed to revoke publish link');
    }
  };

  const handleCopy = async (token: string) => {
    const url = absolutePublishUrl(token);
    try {
      await navigator.clipboard.writeText(url);
      toast.success('Link copied');
    } catch {
      toast.error('Could not copy — select the URL manually.');
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Publish "{report?.name}"</DialogTitle>
          <DialogDescription>
            Create a public URL for anyone to view this report without signing in.
            You can revoke the link at any time.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 pt-2">
          <Label className="text-sm font-medium">New publish link</Label>
          <div className="grid grid-cols-1 md:grid-cols-[1fr_auto_auto] gap-2 items-end">
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">Label</Label>
              <Input
                placeholder="e.g. Q1 all-hands"
                value={label}
                onChange={(e) => setLabel(e.target.value)}
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">Expires (optional)</Label>
              <Input
                type="datetime-local"
                value={expiresAt}
                onChange={(e) => setExpiresAt(e.target.value)}
              />
            </div>
            <Button onClick={() => void handleCreate()} disabled={creating}>
              {creating ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Plus className="h-4 w-4 mr-2" />}
              Create link
            </Button>
          </div>
        </div>

        <div className="pt-4 border-t">
          <Label className="text-sm font-medium">Existing links</Label>
          <div className="border rounded-md mt-2">
            {loading ? (
              <div className="flex items-center justify-center py-6 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Loading...
              </div>
            ) : tokens.length === 0 ? (
              <div className="text-sm text-muted-foreground py-6 text-center">
                No public links yet.
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Label</TableHead>
                    <TableHead className="w-[170px]">Expires</TableHead>
                    <TableHead className="w-[110px] text-right">Views</TableHead>
                    <TableHead className="w-[80px]">Active</TableHead>
                    <TableHead className="w-[110px]" />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {tokens.map((t) => (
                    <TableRow key={t.id}>
                      <TableCell>
                        <div className="text-sm font-medium">{t.label || '(Untitled link)'}</div>
                        <div className="flex items-center gap-1 text-xs text-muted-foreground font-mono truncate max-w-[320px]">
                          <LinkIcon className="h-3 w-3 shrink-0" />
                          {t.token}
                        </div>
                      </TableCell>
                      <TableCell>
                        {t.expires_at
                          ? <span className="text-xs">{formatDateTime(t.expires_at)}</span>
                          : <span className="text-xs text-muted-foreground">Never</span>}
                      </TableCell>
                      <TableCell className="text-right text-xs">
                        {t.access_count}
                        {t.last_accessed_at && (
                          <div className="text-[10px] text-muted-foreground">
                            last {formatDate(t.last_accessed_at)}
                          </div>
                        )}
                      </TableCell>
                      <TableCell>
                        <Switch
                          checked={t.is_active}
                          onCheckedChange={(checked) => void handleToggleActive(t, checked)}
                        />
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1 justify-end">
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7"
                            onClick={() => void handleCopy(t.token)}
                            title="Copy link"
                          >
                            <Copy className="h-3.5 w-3.5" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7"
                            onClick={() => void handleDelete(t)}
                            title="Revoke"
                          >
                            <Trash2 className="h-3.5 w-3.5 text-destructive" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
